import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight, ArrowDown } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1639066648921-82d4500abf1a?crop=entropy&cs=srgb&fm=webp&auto=format,compress&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGRhcmslMjBzZXJ2ZXIlMjByb29tJTIwdGVjaHxlbnwwfHx8fDE3ODU0MDA2MTZ8MA&ixlib=rb-4.1.0&q=85";

const LINE1 = ["Software", "that", "runs"];
const LINE2 = ["your", "business", "—"];
const LINE3 = ["built", "to", "last."];

function MaskedWord({ children, delay }) {
  return (
    <>
      <span className="reveal-line inline-block align-bottom">
        <motion.span
          initial={{ y: "110%" }}
          animate={{ y: "0%" }}
          transition={{ duration: 0.9, delay, ease: [0.7, 0, 0.2, 1] }}
          className="inline-block"
        >
          {children}
        </motion.span>
      </span>
      {" "}
    </>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative pt-24 md:pt-28"
    >
      {/* Top meta strip */}
      <div className="mx-auto max-w-[1440px] w-full px-6 md:px-10 pb-6 md:pb-8">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="flex items-center gap-4"
        >
          <span className="mono-label">[01] — Software Engineering, Pune</span>
          <span className="h-px flex-1 bg-white/10" />
          <span className="mono-label hidden md:inline">beaconitdev.com</span>
        </motion.div>
      </div>

      {/* Full-width image banner */}
      <div className="relative w-full">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.1, ease: [0.7, 0, 0.2, 1] }}
          className="relative w-full h-[74svh] md:h-[82svh] min-h-[520px] overflow-hidden bg-[#0b0e18] grain"
        >
          <motion.img
            src={HERO_IMG}
            alt="Server infrastructure"
            style={{ y, scale }}
            className="absolute inset-0 w-full h-[115%] object-cover opacity-90"
            loading="eager"
          />

          {/* Layered gradients for legibility */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#080A0F] via-[#080A0F]/70 to-[#080A0F]/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#080A0F] via-transparent to-[#080A0F]/50" />

          {/* Radial accent glow */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                "radial-gradient(45% 55% at 22% 55%, rgba(255,59,48,0.10), transparent 70%)",
            }}
          />

          {/* Corner meta */}
          <div className="absolute left-6 md:left-10 top-5 mono-label !text-white/70">
            SYS · 12.09.25
          </div>
          <div className="absolute right-6 md:right-10 top-5 mono-label !text-white/60">
            PUNE / IN — <span className="text-[color:var(--accent)]">ONLINE</span>
          </div>

          {/* Heading + body over image */}
          <div className="relative z-10 mx-auto max-w-[1440px] w-full h-full px-6 md:px-10 flex items-center">
            <div className="max-w-[720px]">
              <h1
                data-testid="hero-heading"
                className="font-display text-[36px] sm:text-[44px] md:text-[56px] lg:text-[64px] leading-[1.02] tracking-[-0.03em]"
              >
                <span className="block">
                  {LINE1.map((w, i) => (
                    <MaskedWord key={w} delay={0.15 + i * 0.06}>{w}</MaskedWord>
                  ))}
                </span>
                <span className="block">
                  {LINE2.map((w, i) => (
                    <MaskedWord key={w} delay={0.38 + i * 0.06}>
                      {w === "—" ? <span className="text-[color:var(--accent)]">—</span> : w}
                    </MaskedWord>
                  ))}
                </span>
                <span className="block text-white/55">
                  {LINE3.map((w, i) => (
                    <MaskedWord key={w} delay={0.58 + i * 0.06}>
                      {i === LINE3.length - 1 ? (
                        <span>
                          {w.slice(0, -1)}
                          <span className="text-[color:var(--accent)]">.</span>
                        </span>
                      ) : (
                        w
                      )}
                    </MaskedWord>
                  ))}
                </span>
              </h1>

              {/* GoHighLevel certification callout */}
              <motion.a
                href="https://directory.gohighlevel.com/india/pune/certified-admins/kiran-kshirsagar"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-ghl-badge"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.85 }}
                className="mt-7 md:mt-9 group/badge inline-flex items-center gap-3 md:gap-4 border border-white/15 hover:border-[color:var(--accent)]/60 bg-black/25 backdrop-blur-sm pl-2.5 pr-4 md:pl-3 md:pr-5 py-2 md:py-2.5 transition-colors max-w-full w-full sm:w-auto"
              >
                <img
                  src="https://customer-assets-agu9un31.emergentagent.net/job_beacon-tech-labs/artifacts/6b4y1mad_09d68805-ef5b-4f16-9b67-ccce0bf65473.webp"
                  alt="HighLevel Certified Admin"
                  className="w-10 h-10 md:w-14 md:h-14 object-contain shrink-0 drop-shadow-[0_4px_14px_rgba(0,0,0,0.55)]"
                  loading="eager"
                />
                <div className="min-w-0 flex-1">
                  <div className="mono-label !text-[color:var(--accent)] !text-[10px]">
                    Certified · verify ↗
                  </div>
                  <div className="mt-0.5 md:mt-1 font-display text-[13px] md:text-[17px] leading-[1.2] tracking-tight text-white">
                    <span className="sm:hidden">HighLevel Certified — funnels &amp; sub-accounts</span>
                    <span className="hidden sm:inline">HighLevel Certified — funnels, pipelines &amp; white-label sub-accounts</span>
                  </div>
                </div>
              </motion.a>

              <motion.p
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.95 }}
                className="mt-6 md:mt-7 max-w-[520px] text-white/70 text-[14px] md:text-[16px] leading-[1.6]"
              >
                A full-stack engineering partner delivering{" "}
                <span className="text-white">custom CRMs, headless eCommerce and API-first
                platforms</span> — plus{" "}
                <span className="text-white">certified GoHighLevel builds</span> for agencies
                scaling client operations.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 1.05 }}
                className="mt-8 flex flex-wrap items-center gap-3 md:gap-4"
              >
                <a
                  href="#contact"
                  data-testid="hero-cta-primary"
                  className="group inline-flex items-center gap-3 bg-[color:var(--accent)] hover:bg-[color:var(--accent-hover)] px-5 md:px-6 py-3.5 md:py-4 text-[13px] md:text-[14px] font-medium tracking-tight"
                >
                  Get a free consultation
                  <ArrowUpRight
                    size={16}
                    className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  />
                </a>
                <a
                  href="#portfolio"
                  data-testid="hero-cta-secondary"
                  className="inline-flex items-center gap-3 border border-white/25 hover:border-white/70 bg-black/20 backdrop-blur-sm px-5 md:px-6 py-3.5 md:py-4 text-[13px] md:text-[14px] tracking-tight transition-colors"
                >
                  View our work
                  <ArrowDown size={16} />
                </a>
              </motion.div>
            </div>
          </div>

          {/* Bottom-right stat block over banner */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.6 }}
            className="absolute right-6 md:right-10 bottom-6 md:bottom-8 hidden sm:flex items-end gap-5"
          >
            <div className="text-right">
              <div className="font-display text-3xl md:text-4xl leading-none">
                13<span className="text-[color:var(--accent)]">+</span>
              </div>
              <div className="mono-label mt-1">Yrs delivering</div>
            </div>
            <span className="h-9 w-px bg-white/20" />
            <div className="text-right">
              <div className="font-display text-3xl md:text-4xl leading-none">
                120<span className="text-[color:var(--accent)]">+</span>
              </div>
              <div className="mono-label mt-1">Projects</div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Below-banner meta strip */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.6 }}
        className="mx-auto max-w-[1440px] w-full px-6 md:px-10 mt-6 md:mt-8 flex items-center gap-4"
      >
        <span className="mono-label">Scroll</span>
        <span className="h-px flex-1 bg-white/10" />
        <span className="mono-label">CRM · eCommerce · APIs · AWS</span>
      </motion.div>
    </section>
  );
}
